import React, { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RESERVATIONS, FORMS } from "../lib/_data";

const DEFAULT_TIME_SLOTS = ["18:00", "18:30", "19:00", "19:30", "20:00", "20:30", "21:00"];
const PARTY_SIZES = [1, 2, 3, 4, 5, 6, 7, 8];
const OCCASIONS = ["No occasion", "Anniversary", "Birthday", "Business", "Celebration", "Other"];

const inputCls =
    "w-full bg-transparent border-0 border-b border-cream-200 rounded-none px-0 py-3 font-serif text-lg text-forest-800 placeholder:text-forest-500/60 placeholder:font-sans placeholder:text-base focus:outline-none focus:border-forest-700 focus:ring-0 transition-colors";

function Field({ label, htmlFor, children, className = "" }) {
    return (
        <div className={className}>
            <label htmlFor={htmlFor} className="label text-forest-500 block mb-3">
                {label}
            </label>
            {children}
        </div>
    );
}

export default function ReservationForm({ slug }) {
    // Iter-77 — all visible form copy is editable via copy_overrides
    // (`forms.reservation.*`). Submission behavior is unchanged.
    const fc = (FORMS && FORMS.reservation) || {};
    const occasions = fc.occasionOptions
        ? [fc.occasionPlaceholder || "No occasion", ...fc.occasionOptions.split(",").map((s) => s.trim()).filter(Boolean)]
        : OCCASIONS;
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        party_size: 2,
        occasion: "No occasion",
        special_requests: "",
    });
    const [date, setDate] = useState();
    const [time, setTime] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);

    // Time slots derive from RESERVATIONS.seatings if the biz specified them,
    // else fall back to a reasonable dinner-service default.
    const timeSlots =
        (RESERVATIONS.seatings || [])
            .map((s) => s.time || s.label || "")
            .filter((t) => /^\d{1,2}:\d{2}$/.test(t)) || [];
    const slots = timeSlots.length ? timeSlots : DEFAULT_TIME_SLOTS;

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const canSubmit =
        form.name.trim().length > 1 &&
        /\S+@\S+\.\S+/.test(form.email) &&
        form.phone.trim().length > 4 &&
        date &&
        time;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit || submitting) return;
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                date: format(date, "yyyy-MM-dd"),
                time,
                occasion: form.occasion === "No occasion" ? null : form.occasion,
                special_requests: form.special_requests || null,
            };
            const { data } = await api.post(`/public/site/${slug}/reservations`, payload);
            setSuccess(data);
            toast.success("Reservation received");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not submit request";
            toast.error(typeof msg === "string" ? msg : "Could not submit request");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setSuccess(null);
        setForm({
            name: "",
            email: "",
            phone: "",
            party_size: 2,
            occasion: "No occasion",
            special_requests: "",
        });
        setDate(undefined);
        setTime("");
    };

    return (
        <div className="bg-cream-50 border border-cream-200 p-8 sm:p-12">
            <AnimatePresence mode="wait">
                {success ? (
                    <motion.div
                        key="success"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.5 }}
                        data-testid="reservation-success"
                        className="py-8"
                    >
                        <div className="w-16 h-16 border border-forest-700 flex items-center justify-center mb-8">
                            <Check className="w-6 h-6 text-forest-700" strokeWidth={1.5} />
                        </div>
                        <p className="label text-forest-500 mb-4" data-testid="success-eyebrow">{fc.successEyebrow || "Request received"}</p>
                        <h3 className="font-serif text-4xl sm:text-5xl tracking-tight text-forest-800 leading-tight">
                            Thank you, <em className="italic">{(success.name || "friend").split(" ")[0]}</em>.
                        </h3>
                        <p className="mt-6 text-forest-500 leading-relaxed max-w-md">
                            We have your request for a table of {success.party_size} on{" "}
                            <span className="text-forest-800">
                                {format(new Date(success.date), "EEEE, d MMMM yyyy")}
                            </span>{" "}
                            at <span className="text-forest-800">{success.time}</span>.
                        </p>
                        <p className="mt-4 text-forest-500 leading-relaxed max-w-md">
                            A member of our team will be in touch at{" "}
                            <span className="text-forest-800">{success.email}</span> within one working day to confirm.
                        </p>
                        <div className="mt-10 pt-8 border-t border-cream-200 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <p className="font-mono text-xs text-forest-500" data-testid="success-ref">
                                Ref · {String(success.id || "").slice(0, 8).toUpperCase()}
                            </p>
                            <button
                                data-testid="reservation-new"
                                onClick={reset}
                                className="label link-underline text-forest-800 sm:ml-auto"
                            >
                                Make another request
                            </button>
                        </div>
                    </motion.div>
                ) : (
                    <motion.form
                        key="form"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        onSubmit={submit}
                        data-testid="reservation-form"
                        className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8"
                    >
                        <Field label={fc.nameLabel || "Full name"} htmlFor="res-name" className="sm:col-span-2">
                            <input
                                id="res-name"
                                data-testid="res-name"
                                value={form.name}
                                onChange={(e) => update("name", e.target.value)}
                                placeholder={fc.namePlaceholder || "Your name"}
                                required
                                className={inputCls}
                            />
                        </Field>

                        <Field label={fc.emailLabel || "Email"} htmlFor="res-email">
                            <input
                                id="res-email"
                                data-testid="res-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                placeholder={fc.emailPlaceholder || "you@email.com"}
                                required
                                className={inputCls}
                            />
                        </Field>

                        <Field label={fc.phoneLabel || "Phone"} htmlFor="res-phone">
                            <input
                                id="res-phone"
                                data-testid="res-phone"
                                type="tel"
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                placeholder={fc.phonePlaceholder || "+1 …"}
                                required
                                className={inputCls}
                            />
                        </Field>

                        <Field label={fc.dateLabel || "Date"}>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        data-testid="res-date-trigger"
                                        className={`${inputCls} flex items-center justify-between text-left`}
                                    >
                                        <span className={date ? "text-forest-800" : "text-forest-500"}>
                                            {date ? format(date, "d MMMM yyyy") : (fc.datePlaceholder || "Select a date")}
                                        </span>
                                        <CalendarIcon className="w-4 h-4 text-forest-500" strokeWidth={1.5} />
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0 bg-cream-50 border-cream-200" align="start">
                                    <Calendar
                                        mode="single"
                                        selected={date}
                                        onSelect={setDate}
                                        disabled={(d) => {
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            return d < today;
                                        }}
                                        initialFocus
                                        data-testid="res-date-calendar"
                                    />
                                </PopoverContent>
                            </Popover>
                        </Field>

                        <Field label={fc.timeLabel || "Time"}>
                            <Select value={time} onValueChange={setTime}>
                                <SelectTrigger data-testid="res-time-trigger" className={inputCls}>
                                    <SelectValue placeholder={fc.timePlaceholder || "Select a seating"} />
                                </SelectTrigger>
                                <SelectContent className="bg-cream-50 border-cream-200">
                                    {slots.map((t) => (
                                        <SelectItem key={t} value={t} data-testid={`res-time-${t}`}>
                                            {t}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label={fc.guestsLabel || "Guests"}>
                            <Select
                                value={String(form.party_size)}
                                onValueChange={(v) => update("party_size", parseInt(v, 10))}
                            >
                                <SelectTrigger data-testid="res-guests-trigger" className={inputCls}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-cream-50 border-cream-200">
                                    {PARTY_SIZES.map((n) => (
                                        <SelectItem key={n} value={String(n)} data-testid={`res-guests-${n}`}>
                                            {n} {n === 1 ? (fc.guestSingular || "guest") : (fc.guestsSuffix || "guests")}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label={fc.occasionLabel || "Occasion"}>
                            <Select value={form.occasion} onValueChange={(v) => update("occasion", v)}>
                                <SelectTrigger data-testid="res-occasion-trigger" className={inputCls}>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-cream-50 border-cream-200">
                                    {occasions.map((o) => (
                                        <SelectItem key={o} value={o}>
                                            {o}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label={fc.notesLabel || "Special requests"} htmlFor="res-notes" className="sm:col-span-2">
                            <textarea
                                id="res-notes"
                                data-testid="res-notes"
                                value={form.special_requests}
                                onChange={(e) => update("special_requests", e.target.value)}
                                placeholder={fc.notesPlaceholder || "Dietary preferences, allergies, accessibility…"}
                                rows={3}
                                className={`${inputCls} resize-none`}
                            />
                        </Field>

                        <div className="sm:col-span-2 pt-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <button
                                type="submit"
                                data-testid="res-submit"
                                disabled={!canSubmit || submitting}
                                className="bg-forest-700 text-cream-50 hover:bg-forest-600 disabled:bg-forest-500 disabled:cursor-not-allowed transition-all duration-500 px-10 py-4 label"
                            >
                                {submitting ? (fc.submittingLabel || "Sending…") : (fc.submitLabel || "Request a table")}
                            </button>
                            <p className="text-xs text-forest-500 leading-relaxed sm:ml-4 max-w-xs">
                                {fc.disclaimer || "Submitting a request does not guarantee a seating. We confirm within 24 hours."}
                            </p>
                        </div>
                    </motion.form>
                )}
            </AnimatePresence>
        </div>
    );
}
